/**
 * PDF Service - Extract text from PDF files using PDF.js
 *
 * Uses Mozilla's PDF.js library for client-side PDF parsing.
 * Supports text-based PDFs (digitally created PDFs).
 */

import * as pdfjsLib from 'pdfjs-dist';

// Set worker source - use unpkg CDN which has correct versioning
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

/**
 * Extract text content from a PDF file
 * @param {File} file - The PDF file to process
 * @returns {Promise<{text: string, pageCount: number, metadata: Object}>}
 */
export async function extractTextFromPDF(file) {
  try {
    // Convert file to ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load the PDF document
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const pageCount = pdf.numPages;
    const textParts = [];
    let metadata = {};

    // Try to get document metadata
    try {
      const meta = await pdf.getMetadata();
      metadata = {
        title: meta.info?.Title || null,
        author: meta.info?.Author || null,
        subject: meta.info?.Subject || null,
        keywords: meta.info?.Keywords || null,
        creationDate: meta.info?.CreationDate || null,
      };
    } catch (e) {
      console.warn('Could not extract PDF metadata:', e);
    }

    // Extract text from each page
    for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Combine text items with proper spacing
      const pageText = textContent.items
        .map((item) => item.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();

      if (pageText) {
        textParts.push(`[Page ${pageNum}]\n${pageText}`);
      }
    }

    const fullText = textParts.join('\n\n');

    return {
      text: fullText,
      pageCount,
      metadata,
      fileName: file.name,
      fileSize: file.size,
    };
  } catch (error) {
    console.error('PDF extraction error:', error);
    throw new Error(`Failed to extract text from PDF: ${error.message}`);
  }
}

/**
 * Extract text from PDF with chunking for large documents
 * Useful for embedding generation where there might be token limits
 * @param {File} file - The PDF file to process
 * @param {number} maxChunkSize - Maximum characters per chunk (default 4000)
 * @returns {Promise<{chunks: string[], pageCount: number, metadata: Object}>}
 */
export async function extractTextChunked(file, maxChunkSize = 4000) {
  const { text, pageCount, metadata, fileName, fileSize } = await extractTextFromPDF(file);

  // If text is small enough, return as single chunk
  if (text.length <= maxChunkSize) {
    return {
      chunks: [text],
      fullText: text,
      pageCount,
      metadata,
      fileName,
      fileSize,
    };
  }

  // Split into chunks at sentence boundaries
  const chunks = [];
  let currentChunk = '';
  const sentences = text.split(/(?<=[.!?])\s+/);

  for (const sentence of sentences) {
    if (currentChunk.length + sentence.length > maxChunkSize && currentChunk.length > 0) {
      chunks.push(currentChunk.trim());
      currentChunk = '';
    }
    currentChunk += sentence + ' ';
  }

  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim());
  }

  return {
    chunks,
    fullText: text,
    pageCount,
    metadata,
    fileName,
    fileSize,
  };
}

/**
 * Check if a file is a PDF
 * @param {File} file - The file to check
 * @returns {boolean}
 */
export function isPDF(file) {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Get a preview summary of the PDF (first N characters)
 * @param {string} text - The extracted text
 * @param {number} maxLength - Maximum preview length
 * @returns {string}
 */
export function getPreviewText(text, maxLength = 500) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

export default {
  extractTextFromPDF,
  extractTextChunked,
  isPDF,
  getPreviewText,
};
